export type OutputFormat = 'webp' | 'jpeg' | 'png';

export interface FileMeta {
  name: string;
  compress: boolean;
  quality: number; // 1–100
  format: OutputFormat;
  compressedSize: number | null;
}

export function defaultMeta(file: File): FileMeta {
  return {
    name: stripExtension(file.name),
    compress: file.type.startsWith('image/'),
    quality: 82,
    format: 'webp',
    compressedSize: null,
  };
}

export function stripExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  return lastDot > 0 ? filename.slice(0, lastDot) : filename;
}

export function formatToMime(format: OutputFormat): string {
  if (format === 'jpeg') return 'image/jpeg';
  if (format === 'png') return 'image/png';
  return 'image/webp';
}

export function formatToExt(format: OutputFormat): string {
  if (format === 'jpeg') return '.jpg';
  if (format === 'png') return '.png';
  return '.webp';
}

export function originalExt(filename: string): string {
  const idx = filename.lastIndexOf('.');
  return idx > 0 ? filename.slice(idx) : '';
}

export async function compressImage(file: File, quality: number, format: OutputFormat): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Canvas context unavailable'));
      ctx.drawImage(img, 0, 0);
      const mime = formatToMime(format);
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error('Canvas toBlob failed'));
          resolve(new File([blob], file.name, { type: mime }));
        },
        mime,
        format === 'png' ? undefined : quality,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')); };
    img.src = url;
  });
}
